/**
 * Fire-and-forget telemetry for the auth funnel.
 *
 * - Sends to POST {API_URL}/events (public endpoint, origin-allowlisted server-side)
 * - Uses sendBeacon when available so events survive page navigation/unload
 * - anonId is a random per-browser id kept in localStorage; never an e-mail
 * - Never throws, never blocks UI
 *
 * Event names must match backend/src/handlers/events/eventNames.js.
 */
import config from "../config/env";

const ANON_KEY = "acig.anon";
const ENDPOINT = `${config.API_URL}/events`;

export const EVENTS = Object.freeze({
  SIGNUP_STARTED: "auth_signup_started",
  SIGNUP_SUBMITTED: "auth_signup_submitted",
  SIGNUP_FAILED: "auth_signup_failed",
  CONFIRM_VIEWED: "auth_confirm_viewed",
  CONFIRM_RESEND: "auth_confirm_resend",
  CONFIRMED: "auth_confirmed",
  CONFIRM_FAILED: "auth_confirm_failed",
  LOGIN_SUCCESS: "auth_login_success",
  LOGIN_FAILED: "auth_login_failed",
  RESET_REQUESTED: "auth_reset_requested",
  RESET_COMPLETED: "auth_reset_completed",
});

const randomId = () => {
  try {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
  }
};

const getAnonId = () => {
  try {
    let id = localStorage.getItem(ANON_KEY);
    if (!id) {
      id = randomId();
      localStorage.setItem(ANON_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
};

export const track = (name, props = {}) => {
  try {
    const payload = JSON.stringify({ name, props, anonId: getAnonId() });
    if (navigator.sendBeacon) {
      const blob = new Blob([payload], { type: "application/json" });
      if (navigator.sendBeacon(ENDPOINT, blob)) return;
    }
    fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
      credentials: "omit",
    }).catch(() => {});
  } catch {
    /* telemetry must never break the app */
  }
};
